// src/hooks/useSchool.js
import { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import { useAuth } from "../context/AuthContext";

/**
 * Loads the school linked to the logged-in schooladmin.
 * Expects users/{uid} to contain { schoolId }.
 * - Returns schoolId, school doc, isActive (boolean), loading.
 * - Persists schoolId in localStorage so it survives refreshes.
 */
export default function useSchool() {
  const { profile } = useAuth();

  // derive schoolId from profile, fall back to localStorage
  const [schoolId, setSchoolId] = useState(() => {
    return profile?.schoolId || localStorage.getItem("schoolId") || null;
  });

  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);

  // keep schoolId in sync with profile + persist
  useEffect(() => {
    if (profile?.schoolId && profile.schoolId !== schoolId) {
      setSchoolId(profile.schoolId);
      localStorage.setItem("schoolId", profile.schoolId);
    }
  }, [profile?.schoolId]); // eslint-disable-line react-hooks/exhaustive-deps

  // load the school doc once when schoolId changes
  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setLoading(true);
        if (!schoolId) {
          if (alive) setSchool(null);
          return;
        }
        const snap = await getDoc(doc(db, "schools", schoolId));
        if (alive) {
          setSchool(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [schoolId]);

  // normalize active flag (supports boolean active or status string)
  const isActive = useMemo(() => {
    if (!school) return false;
    if (typeof school.active === "boolean") return school.active;
    const s = String(school.status || "").trim().toLowerCase();
    return s === "active";
  }, [school]);

  return { schoolId, school, isActive, loading };
}
